/**
 * @fileoverview Input/output schemas for character-arc-extractor-v3.
 *
 * This agent analyzes source text to extract character arcs for major characters.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 * Beats reference their parent character arc by index.
 * Consumer hooks are responsible for assembling the nested domain structure.
 *
 * Beat and arc field definitions are derived from the canonical
 * CharacterBeatSchema and CharacterPlannedArcV2Schema to prevent drift.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  CharacterBeatSchema,
  CharacterPlannedArcV2Schema,
} from '@/domains/characters/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Uses existing prompt variables:
 * - textInput: The source text to analyze for character arcs
 * - characterRoles: Major characters loaded from project (filtered before calling)
 * - storyContext: Story metadata for context
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.textInput.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characterRoles.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  isShortForm: z.boolean().optional().describe(
    'When true, the project is short-form (under ~15,000 words). ' +
    'The agent adjusts beat density and summary length accordingly.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * Character arc metadata without nested beats.
 * Derived from CharacterPlannedArcV2Schema (minus beats) with characterName added.
 * Beats reference arcs by characterIndex.
 */
const CharacterArcOutputSchema = CharacterPlannedArcV2Schema
  .omit({ beats: true })
  .extend({
    characterName: z.string().describe(
      'The exact name of the character this arc belongs to. Must match one of the provided character names.'
    ),
  });

/**
 * Beat with characterIndex reference instead of id.
 * Derived from the canonical CharacterBeatSchema to keep field descriptions in sync.
 * Omits legacy structurePhase; requires globalPhase and arcPhase.
 */
const CharacterBeatOutputSchema = CharacterBeatSchema
  .omit({ id: true, structurePhase: true })
  .required({ globalPhase: true, arcPhase: true })
  .extend({
    characterIndex: z.number().describe(
      'Index of the character this beat belongs to in the characterArcs array (0-based).'
    ),
  });

/**
 * Output schema for the character-arc-extractor-v3 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility:
 * - Level 1: Root object
 * - Level 2: characterArcs/beats arrays
 * - Level 3: Array items
 *
 * Consumer hooks reassemble into nested CharacterPlannedArcV2 entities.
 */
export const OutputSchema = z.object({
  characterArcs: z.array(CharacterArcOutputSchema).describe(
    'Array of character arcs (without beats). Reference beats by characterIndex.'
  ),
  beats: z.array(CharacterBeatOutputSchema).describe(
    'All character arc beats with characterIndex references. ' +
    'Each beat must have both globalPhase (position in overall story timeline) and ' +
    'arcPhase (structural function for this character\'s arc). ' +
    'Each character should have beats at key structural phases (at minimum Initial State and Final State).'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type CharacterArcOutput = z.infer<typeof CharacterArcOutputSchema>;
export type CharacterBeatOutput = z.infer<typeof CharacterBeatOutputSchema>;
